import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { fetchTvStoreCategories, fetchTvStoreProducts, fetchTvStoreProduct, getImageUrl } from "../../redux/content";
import { fetchPurchaseHistory } from "../../redux/subscription";
import { getClientApiUrl } from "../../config/apiConfig";
import "../../components/styles/discoveryCategoryPage.scss";

/**
 * DiscoveryCategoryPage — Handles two modes:
 *
 * 1. Category view (/discover/:uid/:name)
 *    Shows TV Store products within a category (e.g. SVOD Bouquets)
 *
 * 2. Product view (/discover/product/:productId/:name)
 *    Shows movies/content within a specific TV Store product's packages
 *    Per Selorm: "Details page: show movies/series in package"
 */

const DiscoveryCategoryPage = () => {
  const { uid, name, productId } = useParams();
  const navigate = useNavigate();
  const tvStoreCategories = useSelector((state) => state.content.tvStoreCategories);
  const { activeProductIds } = useSelector((state) => state.subscription);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productInfo, setProductInfo] = useState(null); // For product view hero

  const isProductView = !!productId;

  // Fetch categories + check subscription on mount
  useEffect(() => {
    if (tvStoreCategories.length === 0) {
      console.log("[DiscCatPage] No categories in Redux, fetching...");
      fetchTvStoreCategories();
    }
    fetchPurchaseHistory("Active");
  }, [tvStoreCategories.length]);

  // MODE 1: Category view — fetch TV Store products in category
  const category = !isProductView ? tvStoreCategories.find((c) => c.uid === uid) : null;

  useEffect(() => {
    if (isProductView || !category?.tv_store?.length) {
      if (category && !isProductView) setLoading(false);
      return;
    }

    const loadProducts = async () => {
      setLoading(true);
      console.log("[DiscCatPage] Category view: fetching", category.tv_store.length, "products in", category.uid);
      const prods = await fetchTvStoreProducts(category.tv_store);
      setItems(prods);
      setLoading(false);
      console.log("[DiscCatPage] Products loaded:", prods.map((p) => `${p.uid}:${p.name}`).join(", "));
    };

    loadProducts();
  }, [category, isProductView]);

  // MODE 2: Product view — fetch movies inside product's packages
  useEffect(() => {
    if (!isProductView) return;

    const loadProductMovies = async () => {
      setLoading(true);
      setItems([]);
      setProductInfo(null);
      console.log("[DiscCatPage] === Product view: BEGIN load for productId:", productId, "===");

      const accessToken = localStorage.getItem("mango_access_token");
      const userId = localStorage.getItem("mango_user_id");

      try {
        // 1. Fetch product detail for hero info
        const product = await fetchTvStoreProduct(productId);
        if (product) {
          setProductInfo(product);
          const optsSummary = product.tv_store_options?.map(
            (o) => `${o.uid}->[${o.packages?.map((p) => p.uid).join(",") || "no-pkgs"}]`
          ).join(" | ") || "no-options";
          console.log("[DiscCatPage] step1 product detail:", product.uid, "/", product.name,
            "big_img:", product.big_image_id, "tv_store_options:", optsSummary);
        } else {
          console.warn("[DiscCatPage] step1 fetchTvStoreProduct returned null for productId", productId);
        }

        // 2. Get package IDs from user TV Store v2 (has numeric IDs)
        const userStoreRes = await fetch(`${getClientApiUrl()}/api/client/v2/9mobile/users/${userId}/tv_store`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userStoreData = await userStoreRes.json();
        const v2Keys = Object.keys(userStoreData.data || {});
        console.log("[DiscCatPage] step2 v2 user_tv_store: total keys =", v2Keys.length,
          "looking up productId:", productId, "(typeof:", typeof productId, ")",
          "first keys:", v2Keys.slice(0, 20).join(","));

        const userProduct = userStoreData.data?.[productId];
        if (!userProduct) {
          console.warn("[DiscCatPage] step2 productId", productId,
            "NOT FOUND in v2 user_tv_store map. All keys:", v2Keys.join(","));
        } else {
          const poSummary = userProduct.price_options?.map(
            (po) => `${po.uid}->ids:[${po.packages?.map((p) => p.id).join(",") || "no-pkgs"}]`
          ).join(" | ") || "no-price-options";
          console.log("[DiscCatPage] step2 v2 entry for", productId, ":", userProduct.uid,
            "action:", userProduct.action, "price_options:", poSummary);
        }

        const rawPackageIds = userProduct?.price_options?.flatMap((po) => po.packages?.map((p) => p.id)) || [];
        const packageIds = rawPackageIds.filter((id) => id != null);
        console.log("[DiscCatPage] step3 rawPackageIds:", JSON.stringify(rawPackageIds),
          "filtered packageIds:", JSON.stringify(packageIds));

        if (packageIds.length === 0) {
          console.warn("[DiscCatPage] step3 No valid package IDs for productId", productId, "— aborting movies fetch");
          setItems([]);
          setLoading(false);
          return;
        }

        // 3. Fetch movies in those packages (per Selorm: "show movies/series in package")
        const moviesUrl = `${getClientApiUrl()}/api/client/v2/9mobile/users/${userId}/movies?packages=${packageIds.join(",")}`;
        console.log("[DiscCatPage] step4 GET movies URL:", moviesUrl);
        const moviesRes = await fetch(moviesUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
        const moviesData = await moviesRes.json();
        console.log("[DiscCatPage] step4 movies?packages= response status:", moviesData.status,
          "total movie count:", moviesData.data?.length || 0,
          "first 15 ids:", (moviesData.data || []).slice(0, 15).map((m) => m.id).join(","));
        console.log("[DiscCatPage] step4b per-movie packages[] (first 9):",
          (moviesData.data || []).slice(0, 9).map((m) => `${m.id}->pkgs:[${(m.packages || []).join(",")}]`).join(" | "));

        // Same client-side package-intersection filter applied to both movies and
        // series: the v2 ...?packages= endpoints ignore the filter (backend bug) but
        // each item's own packages[] field is accurate.
        const requestedSet = new Set(packageIds.map(Number));
        const inRequestedPackages = (item) =>
          Array.isArray(item.packages) && item.packages.some((p) => requestedSet.has(Number(p)));

        // 3a. Movies in those packages (per Selorm: "show movies/series in package")
        const loadMovies = async () => {
          if (moviesData.status !== "ok" || !moviesData.data) return [];
          const matched = moviesData.data.filter(inRequestedPackages);
          console.log("[DiscCatPage] step4c movies package-intersection filter:",
            moviesData.data.length, "from backend ->", matched.length,
            "matching packages", packageIds.join(","));
          if (matched.length === 0) return [];

          // User movies v2 only returns {id, packages, purchased, favorited} — fetch detail for titles/images
          const movieIds = matched.slice(0, 50).map((m) => m.id);
          console.log("[DiscCatPage] step5 fetching details for", movieIds.length, "matched movies");
          const movieDetails = await Promise.all(
            movieIds.map(async (id) => {
              try {
                const res = await fetch(`${getClientApiUrl()}/api/client/v1/9mobile/users/${userId}/movies/${id}`, {
                  headers: { Authorization: `Bearer ${accessToken}` },
                });
                const data = await res.json();
                return data.data ? { ...data.data, _contentType: "movie" } : null;
              } catch {
                return null;
              }
            })
          );
          return movieDetails.filter(Boolean);
        };

        // 3b. Series in those packages — mirrors fetchPodcasts: user series list -> full details
        const loadSeries = async () => {
          try {
            const userSeriesRes = await fetch(
              `${getClientApiUrl()}/api/client/v1/9mobile/users/${userId}/series?packages=${packageIds.join(",")}`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const userSeriesData = await userSeriesRes.json();
            if (userSeriesData.status !== "ok" || !userSeriesData.data?.length) {
              console.log("[DiscCatPage] step4-series no user series available");
              return [];
            }
            const matched = userSeriesData.data.filter(inRequestedPackages);
            console.log("[DiscCatPage] step4c-series package-intersection filter:",
              userSeriesData.data.length, "from backend ->", matched.length);
            if (matched.length === 0) return [];

            // user series only returns {id, packages, ...} — fetch full details for titles/images
            const seriesIds = matched.slice(0, 50).map((s) => s.id).join(",");
            console.log("[DiscCatPage] step5-series fetching details for IDs:", seriesIds);
            const seriesDetailRes = await fetch(
              `${getClientApiUrl()}/api/client/v1/9mobile/series?series_id=${seriesIds}`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const seriesDetailData = await seriesDetailRes.json();
            if (seriesDetailData.status !== "ok" || !seriesDetailData.data?.length) return [];
            return seriesDetailData.data.map((s) => ({ ...s, _contentType: "series" }));
          } catch (err) {
            console.warn("[DiscCatPage] step-series error:", err.message);
            return [];
          }
        };

        const [movieItems, seriesItems] = await Promise.all([loadMovies(), loadSeries()]);
        const combined = [...movieItems, ...seriesItems];
        console.log("[DiscCatPage] step6 LOADED for productId", productId, ":",
          movieItems.length, "movies +", seriesItems.length, "series =", combined.length, "items. First 5:",
          combined.slice(0, 5).map((m) => m.title || m.uid || `id:${m.id}`).join(" | "));
        setItems(combined);
      } catch (e) {
        console.error("[DiscCatPage] Error loading product movies:", e.message);
      }

      setLoading(false);
      console.log("[DiscCatPage] === Product view: END load for productId:", productId, "===");
    };

    loadProductMovies();
  }, [productId, isProductView]);

  // Hero
  const heroBg = isProductView
    ? productInfo?.big_image_id
      ? getImageUrl({ image_id: productInfo.big_image_id })
      : "/assets/showbox/showbox-bg.webp"
    : category?.heading_image_id
    ? getImageUrl({ image_id: category.heading_image_id })
    : "/assets/showbox/showbox-bg.webp";

  const heroTitle = isProductView
    ? productInfo?.name || name || "Collection"
    : category?.name || name || uid || "Category";

  console.log("[DiscCatPage] mode:", isProductView ? "product" : "category", "items:", items.length, "loading:", loading);

  return (
    <div className="disc-cat-page">
      <Navbar activeTab="Discover" />

      {/* Hero Section */}
      <section className="disc-cat-page__hero">
        <div className="disc-cat-page__hero-bg">
          <img src={heroBg} alt="" className="disc-cat-page__hero-bg-img" />
          <div className="disc-cat-page__hero-tint" />
          <div className="disc-cat-page__hero-gradient-top" />
          <div className="disc-cat-page__hero-gradient-bottom" />
        </div>

        <div className="disc-cat-page__hero-content">
          <h1 className="disc-cat-page__hero-title">
            Subscribe to<br />{heroTitle}
          </h1>
          {!(isProductView && activeProductIds.includes(String(productId))) && (
            <button
              className="disc-cat-page__subscribe-btn"
              onClick={() => {
                const targetProductId = isProductView ? productId : null;
                if (targetProductId) {
                  console.log("[DiscCatPage] Subscribe Now → pick plan for product:", targetProductId);
                  navigate(`/discover/product/${targetProductId}/plan`, {
                    state: { returnPath: `/discover/product/${productId}/${name}` },
                  });
                } else {
                  navigate("/settings/subscription");
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M6 4L16 10L6 16V4Z" fill="#0a0a0a" />
              </svg>
              Subscribe Now
            </button>
          )}
        </div>
      </section>

      {/* Content Grid */}
      <section className="disc-cat-page__grid">
        {loading && items.length === 0 &&
          Array.from({ length: 12 }).map((_, i) => (
            <div key={`skel-${i}`} className="disc-cat-page__poster disc-cat-page__poster--skeleton">
              <div className="disc-cat-page__poster-shimmer" />
            </div>
          ))
        }
        {!loading && items.length === 0 && (
          <p className="disc-cat-page__loading">
            No {isProductView ? "content" : "products"} available
          </p>
        )}
        {items.map((item, i) => {
          // Product view: items are movie objects (image_store_id) or series
          //   objects (images.POSTER), tagged with _contentType.
          // Category view: items are TV Store products with card_image_id, name
          const seriesPosterId = item.images?.POSTER;
          const posterUrl = isProductView
            ? item.image_store_id
              ? getImageUrl({ image_id: item.image_store_id })
              : seriesPosterId
              ? getImageUrl({ image_id: seriesPosterId })
              : null
            : item.card_image_id
            ? getImageUrl({ image_id: item.card_image_id })
            : item.big_image_id
            ? getImageUrl({ image_id: item.big_image_id })
            : null;

          const itemTitle = item.title || item.name || item.uid || "";

          return (
            <div
              key={item.id || i}
              className="disc-cat-page__poster"
              onClick={() => {
                if (isProductView) {
                  console.log("[DiscCatPage]", item._contentType, "clicked:", item.id, item.title, "productId:", productId);
                  navigate(`/details/${item.id}`, {
                    state: { fromTab: "Discover", tvStoreProductId: productId, contentType: item._contentType },
                  });
                } else {
                  const slug = (item.name || item.uid).toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
                  console.log("[DiscCatPage] Product clicked:", item.id, item.name);
                  navigate(`/discover/product/${item.id}/${slug}`);
                }
              }}
              role="button"
              tabIndex={0}
              title={itemTitle}
            >
              {posterUrl ? (
                <img src={posterUrl} alt={itemTitle} className="disc-cat-page__poster-img" />
              ) : (
                <div className="disc-cat-page__poster-placeholder">
                  <span>{itemTitle}</span>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <Footer />
    </div>
  );
};

export default DiscoveryCategoryPage;
